import { Head, Link } from "@inertiajs/react";
import { ArrowLeft, Plus, Trash2, Users, UsersRound } from "lucide-react";
import { useState } from "react";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  bizminer_admin_organization_path,
  bizminer_admin_organization_user_path,
} from "@/routes";
import type {
  BizminerAdminOrganization,
  BizminerUser,
} from "@/types/serializers";
import BulkCreateDialog from "./_components/BulkCreateDialog";
import BulkDeleteDialog from "./_components/BulkDeleteDialog";
import CreateUserDialog from "./_components/CreateUserDialog";

interface Props {
  organization: BizminerAdminOrganization;
  users: BizminerUser[];
}

const ROLE_CONFIG: Record<string, { bg: string; text: string; label: string }> =
  {
    pending: {
      bg: "bg-yellow-500/10",
      text: "text-yellow-600",
      label: "Pending",
    },
    viewer: { bg: "bg-blue-500/10", text: "text-blue-600", label: "Viewer" },
    member: { bg: "bg-green-500/10", text: "text-green-600", label: "Member" },
    admin: { bg: "bg-purple-500/10", text: "text-purple-600", label: "Admin" },
  };

function RoleBadge({ role }: { role: string }) {
  const { bg, text, label } = ROLE_CONFIG[role] || ROLE_CONFIG.member;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}
    >
      {label}
    </span>
  );
}

function PageHeader({
  organization,
  onAddUser,
  onBulkAdd,
}: {
  organization: BizminerAdminOrganization;
  onAddUser: () => void;
  onBulkAdd: () => void;
}) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center py-6">
        <div className="flex items-center space-x-4">
          <Link href={bizminer_admin_organization_path(organization.id)}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to {organization.name}
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-foreground">Users</h1>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={onBulkAdd}>
            <UsersRound className="h-4 w-4 mr-2" />
            Bulk Add
          </Button>
          <Button size="sm" onClick={onAddUser}>
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>
    </div>
  );
}

function BulkActionToolbar({
  count,
  onDelete,
  onClear,
}: {
  count: number;
  onDelete: () => void;
  onClear: () => void;
}) {
  return (
    <div className="bg-muted/50 border-b px-6 py-3 flex items-center justify-between">
      <span className="text-sm text-muted-foreground">
        {count} {count === 1 ? "user" : "users"} selected
      </span>
      <div className="flex items-center space-x-2">
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear selection
        </Button>
        <Button variant="destructive" size="sm" onClick={onDelete}>
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Selected
        </Button>
      </div>
    </div>
  );
}

function UserRow({
  user,
  organizationId,
  isSelected,
  onToggle,
}: {
  user: BizminerUser;
  organizationId: number;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <TableRow>
      <TableCell>
        <Checkbox checked={isSelected} onCheckedChange={onToggle} />
      </TableCell>
      <TableCell>
        <Link
          href={bizminer_admin_organization_user_path(organizationId, user.id)}
        >
          <div className="text-sm font-medium text-foreground hover:text-primary">
            {user.name}
          </div>
          <div className="text-sm text-muted-foreground">
            {user.email_address}
          </div>
        </Link>
      </TableCell>
      <TableCell>
        <RoleBadge role={user.role} />
      </TableCell>
      <TableCell className="text-muted-foreground">
        {user.last_session || "Never"}
      </TableCell>
      <TableCell className="text-muted-foreground">{user.created_at}</TableCell>
    </TableRow>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12 text-muted-foreground">
      <Users className="mx-auto h-12 w-12 mb-4" />
      <p>No users found for this organization</p>
    </div>
  );
}

function UsersTable({
  users,
  organizationId,
  selectedIds,
  onToggle,
  onToggleAll,
}: {
  users: BizminerUser[];
  organizationId: number;
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
}) {
  const allSelected = users.length > 0 && selectedIds.size === users.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < users.length;

  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-muted hover:bg-muted">
          <TableHead className="w-10">
            <Checkbox
              checked={
                allSelected ? true : someSelected ? "indeterminate" : false
              }
              onCheckedChange={onToggleAll}
            />
          </TableHead>
          <TableHead>User</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Last Session</TableHead>
          <TableHead>Member Since</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => (
          <UserRow
            key={user.id}
            user={user}
            organizationId={organizationId}
            isSelected={selectedIds.has(user.id)}
            onToggle={() => onToggle(user.id)}
          />
        ))}
      </TableBody>
    </Table>
  );
}

export default function Index({ organization, users }: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBulkCreateDialog, setShowBulkCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const toggleUser = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(
      selectedIds.size === users.length
        ? new Set()
        : new Set(users.map((u) => u.id)),
    );
  };

  const clearSelection = () => setSelectedIds(new Set());
  const selectedUsers = users.filter((u) => selectedIds.has(u.id));

  return (
    <BizminerAdminLayout>
      <Head title={`Users - ${organization.name} - Super Admin`} />

      <div className="min-h-screen">
        <PageHeader
          organization={organization}
          onAddUser={() => setShowCreateDialog(true)}
          onBulkAdd={() => setShowBulkCreateDialog(true)}
        />

        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <Card>
              {selectedIds.size > 0 && (
                <BulkActionToolbar
                  count={selectedIds.size}
                  onDelete={() => setShowDeleteDialog(true)}
                  onClear={clearSelection}
                />
              )}

              {users.length === 0 ? (
                <EmptyState />
              ) : (
                <UsersTable
                  users={users}
                  organizationId={organization.id}
                  selectedIds={selectedIds}
                  onToggle={toggleUser}
                  onToggleAll={toggleAll}
                />
              )}
            </Card>
          </div>
        </div>
      </div>

      <CreateUserDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        organizationId={organization.id}
      />

      <BulkCreateDialog
        open={showBulkCreateDialog}
        onOpenChange={setShowBulkCreateDialog}
        organizationId={organization.id}
      />

      <BulkDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        organizationId={organization.id}
        users={selectedUsers}
        onSuccess={clearSelection}
      />
    </BizminerAdminLayout>
  );
}
